<template>
  <div>
    <v-card class="mb-6 elevation-1">
      <v-card-title class="d-flex align-center py-4 px-6">
        <v-icon color="warning" class="me-2">mdi-clock-alert-outline</v-icon>
        <span class="font-weight-bold">طلبات البدء المتأخر</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined small @click="fetchRequests" class="ms-2">
          <v-icon left size="16">mdi-refresh</v-icon>
          تحديث
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <!-- Status Filter Tabs -->
      <v-tabs v-model="activeTab" class="px-4" @change="fetchRequests">
        <v-tab key="all">الكل ({{ allCount }})</v-tab>
        <v-tab key="pending">
          <v-badge inline color="warning" :content="pendingCount" v-if="pendingCount > 0">
            معلقة
          </v-badge>
          <span v-else>معلقة</span>
        </v-tab>
        <v-tab key="approved">مقبولة ({{ approvedCount }})</v-tab>
        <v-tab key="rejected">مرفوضة ({{ rejectedCount }})</v-tab>
      </v-tabs>
    </v-card>

    <v-card elevation="1">
      <v-card-title class="pa-4">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث في طلبات البدء المتأخر..."
          single-line
          hide-details
          outlined
          dense
        ></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredRequests"
        :search="search"
        :loading="loading"
        loading-text="جاري تحميل الطلبات..."
        no-data-text="لا توجد طلبات بدء متأخر"
        class="elevation-0"
      >
        <template v-slot:item.planned_trip_id="{ item }">
          <span class="font-weight-bold">#{{ item.planned_trip_id }}</span>
        </template>

        <template v-slot:item.driver_name="{ item }">
          <div>
            <div class="font-weight-bold text-subtitle-2">{{ item.driver_name }}</div>
            <small class="text-caption text-muted">{{ item.driver_phone }}</small>
          </div>
        </template>

        <template v-slot:item.delay_minutes="{ item }">
          <v-chip color="error" label x-small class="font-weight-bold">
            تأخير {{ item.delay_minutes }} دقيقة
          </v-chip>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            dark
            small
            class="font-weight-bold text-capitalize"
          >
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div v-if="item.status === 'pending'" class="d-flex align-center">
            <v-btn
              color="success"
              small
              depressed
              class="me-2"
              :loading="actionLoading === item.id"
              @click="approveRequest(item)"
            >
              <v-icon left size="16">mdi-check-circle-outline</v-icon>
              قبول
            </v-btn>
            <v-btn
              color="error"
              small
              outlined
              :loading="actionLoading === item.id"
              @click="openRejectModal(item)"
            >
              <v-icon left size="16">mdi-close-circle-outline</v-icon>
              رفض
            </v-btn>
          </div>
          <div v-else-if="item.status === 'rejected' && item.rejection_reason">
            <small class="error--text">{{ item.rejection_reason }}</small>
          </div>
          <div v-else>
            <small class="text-muted">مراجعة: {{ item.reviewed_by || 'الإدارة' }}</small>
          </div>
        </template>
      </data-table>
    </v-card>

    <!-- Reject Reason Dialog -->
    <v-dialog v-model="rejectDialog" max-width="450">
      <v-card>
        <v-card-title class="headline error--text">
          <v-icon color="error" class="me-2">mdi-alert-circle-outline</v-icon>
          رفض طلب البدء المتأخر
        </v-card-title>
        <v-card-text class="pt-4">
          <p>هل أنت تأكد من رفض طلب البدء المتأخر للسائق <strong>{{ selectedRequest ? selectedRequest.driver_name : '' }}</strong> للرحلة رقم #{{ selectedRequest ? selectedRequest.planned_trip_id : '' }}؟</p>
          <v-textarea
            v-model="rejectionReason"
            label="سبب الرفض (اختياري)"
            outlined
            rows="3"
            placeholder="اكتب سبب رفض طلب البدء المتأخر..."
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="rejectDialog = false">إلغاء</v-btn>
          <v-btn color="error" depressed :loading="submittingReject" @click="confirmReject">
            تأكيد الرفض
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'LateStartRequests',
  data() {
    return {
      activeTab: 1, // Default to Pending
      search: '',
      loading: false,
      actionLoading: null,
      requests: [],
      rejectDialog: false,
      selectedRequest: null,
      rejectionReason: '',
      submittingReject: false,
      headers: [
        { text: 'رقم الرحلة', value: 'planned_trip_id', align: 'start' },
        { text: 'السائق', value: 'driver_name' },
        { text: 'المسار', value: 'route_name' },
        { text: 'موعد الرحلة الرسمي', value: 'scheduled_start_time' },
        { text: 'بداية التحرك المتوقعة', value: 'expected_movement_start' },
        { text: 'وقت الطلب', value: 'requested_at' },
        { text: 'التأخير', value: 'delay_minutes' },
        { text: 'الحالة', value: 'status', align: 'center' },
        { text: 'الإجراءات', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },
  computed: {
    allCount() {
      return this.requests.length;
    },
    pendingCount() {
      return this.requests.filter(r => r.status === 'pending').length;
    },
    approvedCount() {
      return this.requests.filter(r => r.status === 'approved').length;
    },
    rejectedCount() {
      return this.requests.filter(r => r.status === 'rejected').length;
    },
    filteredRequests() {
      if (this.activeTab === 1) {
        return this.requests.filter(r => r.status === 'pending');
      } else if (this.activeTab === 2) {
        return this.requests.filter(r => r.status === 'approved');
      } else if (this.activeTab === 3) {
        return this.requests.filter(r => r.status === 'rejected');
      }
      return this.requests;
    },
  },
  mounted() {
    this.fetchRequests();
  },
  methods: {
    fetchRequests() {
      this.loading = true;
      axios
        .get('/admin/late-start-requests')
        .then((response) => {
          this.loading = false;
          if (response.data && response.data.requests) {
            this.requests = response.data.requests;
          }
        })
        .catch((error) => {
          this.loading = false;
          console.error('Error fetching late start requests:', error);
        });
    },
    approveRequest(item) {
      this.actionLoading = item.id;
      axios
        .post(`/admin/late-start-requests/${item.id}/approve`)
        .then((response) => {
          this.actionLoading = null;
          this.$notify({
            title: 'نجاح',
            text: 'تمت الموافقة على طلب البدء المتأخر بنجاح.',
            type: 'success',
          });
          this.fetchRequests();
        })
        .catch((error) => {
          this.actionLoading = null;
          console.error('Error approving request:', error);
          this.$swal('خطأ', 'حدث خطأ أثناء الموافقة على الطلب', 'error');
        });
    },
    openRejectModal(item) {
      this.selectedRequest = item;
      this.rejectionReason = '';
      this.rejectDialog = true;
    },
    confirmReject() {
      if (!this.selectedRequest) return;
      this.submittingReject = true;
      axios
        .post(`/admin/late-start-requests/${this.selectedRequest.id}/reject`, {
          rejection_reason: this.rejectionReason,
        })
        .then((response) => {
          this.submittingReject = false;
          this.rejectDialog = false;
          this.$notify({
            title: 'تم الرفض',
            text: 'تم رفض طلب البدء المتأخر.',
            type: 'warn',
          });
          this.fetchRequests();
        })
        .catch((error) => {
          this.submittingReject = false;
          console.error('Error rejecting request:', error);
          this.$swal('خطأ', 'حدث خطأ أثناء رفض الطلب', 'error');
        });
    },
    getStatusColor(status) {
      switch (status) {
        case 'pending':
          return 'warning';
        case 'approved':
          return 'success';
        case 'rejected':
          return 'error';
        default:
          return 'grey';
      }
    },
    getStatusLabel(status) {
      switch (status) {
        case 'pending':
          return 'معلق';
        case 'approved':
          return 'مقبول';
        case 'rejected':
          return 'مرفوض';
        default:
          return status;
      }
    },
  },
};
</script>
